/**
 * Error thrown when a document is not found in Relativity (404).
 * This should be treated as "skipped" rather than "failed".
 */
export class DocumentNotFoundError extends Error {
  constructor(
    public readonly documentArtifactId: number,
    public readonly workspaceId: number,
  ) {
    super(`Document ${documentArtifactId} not found in workspace ${workspaceId}`);
    this.name = 'DocumentNotFoundError';
  }
}

/**
 * Error thrown for retryable API errors (429, 502, 503).
 */
export class RetryableApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly retryAfterMs?: number,
  ) {
    super(`Retryable API error: ${statusCode}`);
    this.name = 'RetryableApiError';
  }
}

/**
 * Error codes and response shape for the Create Relativity List endpoint.
 * Shared between the backend (when building error responses) and the frontend
 * (when rendering the user-facing message for each case).
 */
export const CreateRelativityListErrorCode = {
  MissingRelativityArtifacts: 'MISSING_RELATIVITY_ARTIFACTS',
  DocsNotFound: 'DOCS_NOT_FOUND',
  RelativityPermissionDenied: 'RELATIVITY_PERMISSION_DENIED',
  RelativityNotFound: 'RELATIVITY_NOT_FOUND',
  RelativityBadRequest: 'RELATIVITY_BAD_REQUEST',
  RelativityUnavailable: 'RELATIVITY_UNAVAILABLE',
  RelativityUnexpectedResponse: 'RELATIVITY_UNEXPECTED_RESPONSE',
} as const;

export type CreateRelativityListErrorCode =
  (typeof CreateRelativityListErrorCode)[keyof typeof CreateRelativityListErrorCode];

/** Shape of the JSON body returned from the endpoint when create-list fails. */
export interface CreateRelativityListErrorBody {
  message: string;
  code?: CreateRelativityListErrorCode;
  /** Docs that exist but have no Relativity artifact ID. */
  missingDocs?: { id: string; name: string }[];
  /** Doc IDs that no longer exist (concurrent delete). */
  notFoundDocIds?: string[];
  /** Raw error message extracted from the Relativity API response, if any. */
  relativityMessage?: string | null;
}

/**
 * Thrown by the frontend mutation hook when the Create Relativity List
 * endpoint returns a non-OK response. Preserves HTTP status + parsed body
 * so the dialog can branch on `body.code`.
 */
export class CreateRelativityListError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: CreateRelativityListErrorBody,
  ) {
    super(body.message);
    this.name = 'CreateRelativityListError';
  }
}
